/** Success screen after a completed B2C reschedule (PLA-878, prototype `.done`). */
import { FC, useEffect, useRef } from "react"
import { Animated, Easing, TextStyle, View, ViewStyle } from "react-native"
import Icon from "react-native-vector-icons/MaterialIcons"

import { Text } from "@/components/Text"

import { ActionButton } from "./ActionButton"
import { prefersReducedMotion } from "./motion"
import type { ConfirmData } from "./rescheduleSlotModel"
import { font, tokens } from "./tokens"

export const SuccessView: FC<{ data: ConfirmData; onDone: () => void }> = ({ data, onDone }) => {
  const pop = useRef(new Animated.Value(prefersReducedMotion() ? 1 : 0)).current

  useEffect(() => {
    if (prefersReducedMotion()) return
    Animated.timing(pop, {
      toValue: 1,
      duration: 350,
      easing: Easing.bezier(0.2, 1.4, 0.4, 1),
      useNativeDriver: false,
    }).start()
  }, [pop])

  return (
    <View style={$wrap}>
      <View style={$center} accessibilityRole="alert" testID="success-view">
        <Animated.View
          style={[
            $circle,
            {
              opacity: pop,
              transform: [
                { scale: pop.interpolate({ inputRange: [0, 1], outputRange: [0.4, 1] }) },
              ],
            },
          ]}
          testID="success-circle"
        >
          <Icon name="check" size={34} color={tokens.surface} />
        </Animated.View>
        <Text style={$title}>Ihr neuer Termin steht</Text>
        <Text style={$when}>
          {data.fullDate} · {data.time} Uhr
        </Text>
        <Text style={$body}>{data.doneText}</Text>
      </View>
      <View style={$buttons}>
        <ActionButton label="Fertig" variant="primary" onPress={onDone} testID="success-done" />
      </View>
    </View>
  )
}

const $wrap: ViewStyle = { gap: 16 }
const $center: ViewStyle = { alignItems: "center", gap: 10, paddingTop: 12, paddingBottom: 4 }
const $circle: ViewStyle = {
  width: 64,
  height: 64,
  borderRadius: 32,
  backgroundColor: tokens.green,
  alignItems: "center",
  justifyContent: "center",
}
const $title: TextStyle = {
  marginTop: 6,
  fontSize: 20,
  fontFamily: font.bold,
  color: tokens.ink,
  textAlign: "center",
}
const $when: TextStyle = {
  fontSize: 16,
  fontFamily: font.semi,
  color: tokens.ink,
  textAlign: "center",
}
const $body: TextStyle = {
  maxWidth: 300,
  fontSize: 14,
  lineHeight: 20,
  fontFamily: font.regular,
  color: tokens.ink70,
  textAlign: "center",
}
const $buttons: ViewStyle = { flexDirection: "row", gap: 12 }
